import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { History } from "history";
import { Switch, Route, Redirect } from "react-router-dom";
import Header from "../../components/common/header";
import Sidebar from "../../components/common/sidebar";
import TabletMenuToggle from "../../components/common/TabletMenuToggle";
import AppRoutes from "../routing";
import { useZoomAwareSidebar } from "../../hooks/useZoomAwareResponsive";

import { routes } from "../routes";
import NotificationsRouter from "../Notifications";
import initializedFirebaseApp from "../../utils/firebase";
import firebase from "firebase";
import notificationService from "../../services/notificationService";
import { IApplicationState } from "../../redux/reducers";

import userService from "../../services/userService";
import { IRoute } from "../../interfaces/route";
import Search from "../search";
import Walkthrough from "../../components/common/walkthrough/walkthrough";
import SupplierDashboard from "../supplierDashboard/components/supplierDashboard";
import { SupplierRoutes } from "../supplierRoutes";
import Dashboard from "../newDashboard/dashboard/dashboard";
import Account from "../account";
import { canAccessViewMode, validateAndCorrectViewMode, isSubscriptionExpired } from "../../utils/subscription";
import { ViewMode } from "../../redux/actions/user/userAction";
import { setViewModeAction } from "../../redux/actions/user/userActions";
import { Dispatch } from "redux";

interface IAppProps {
	history: History;
	location: any;
	profile: any;
	currentViewMode: "supplier" | "manufacturer";
	permissions?: any;
	saveDeviceToken: (token: string) => Promise<any>;
	getUserProfile: () => Promise<any>;
	correctViewMode: (mode: ViewMode) => void;
}

const App: React.FC<IAppProps> = (props) => {
	const [isDashboard, setIsDashboard] = useState(props.location.pathname === "/app/dashboard");
	const [requestedProfile, setRequestedProfile] = useState<boolean>();
	const { sidebarType, isSidebarOpen, handleSidebarToggle } = useZoomAwareSidebar();

	// Track window width for responsive layout
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

	// Check if we're on a profile/account page to hide header
	const isProfilePage = props.location.pathname.includes('/app/account');
	const isSubscribePage = props.location.pathname.includes('/app/subscribe');

	// Update isMobile on window resize
	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		setIsDashboard(props.location.pathname === "/app/dashboard");
	}, [props.location.pathname]);

	props.history.listen((location, action) => {
		if (
			window.innerHeight < 767 &&
			document.getElementById("main-wrapper")?.className.indexOf("show-sidebar") !== -1
		) {
			// collapse sidebar on mobile if needed
		}
	});

	// 🔔 Setup Firebase push notifications
	useEffect(() => {
		const accessToken = localStorage.getItem("access_token");
		if (!accessToken) return;

		// Only setup messaging if logged in or has a token (optional check)
		if (!firebase.messaging.isSupported()) return;
		const messaging = initializedFirebaseApp.messaging();

		Notification.requestPermission()
			.then((permission) => {
				if (permission === "granted") {
					return messaging.getToken();
				}
			})
			.then((token) => {
				if (token && props.saveDeviceToken) props.saveDeviceToken(token);
			})
			.catch(() => {
				// Silent fail for push notification registration
			});
	}, [props.profile]); // Re-run if profile changes

	// 👤 Fetch user profile + setup zoom-aware responsive handling
	useEffect(() => {
		// Only fetch profile if a token exists in storage
		const token = localStorage.getItem("access_token");
		if (token && !requestedProfile) {
			Promise.all([props.getUserProfile()]).finally(() => {
				setRequestedProfile(true);
			});
		} else if (!token) {
			setRequestedProfile(true); // Don't block loading if no token
		}
	}, [props.profile, requestedProfile]);

	// 🔄 Only correct view mode if subscription expired (not for access restrictions)
	// Toggle is always functional - data will be faded if not subscribed
	// Don't redirect to billing on initial load - let users see dashboard with faded data
	// Only run this check once on initial profile load, not on every profile update
	const [hasCheckedSubscription, setHasCheckedSubscription] = useState(false);

	useEffect(() => {
		if (props.profile && props.currentViewMode && !hasCheckedSubscription) {
			// Only correct if subscription expired - otherwise allow any view mode
			if (isSubscriptionExpired(props.profile)) {
				// Force manufacturer view if subscription expired
				if (props.currentViewMode !== "manufacturer") {
					props.correctViewMode("manufacturer");
				}
				// Don't redirect to billing automatically - let users see dashboard with faded data
				// They can navigate to billing manually if needed
			}
			setHasCheckedSubscription(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.profile, hasCheckedSubscription]);

	// 🎯 Navigate to correct dashboard on initial load based on subscription type
	// This ensures supplier users are navigated to supplier dashboard initially
	const [hasHandledInitialNavigation, setHasHandledInitialNavigation] = useState(false);

	useEffect(() => {
		if (props.profile && props.currentViewMode && !hasHandledInitialNavigation) {
			// Check if there was a saved preference before profile loaded (if not, this is initial load)
			// We use a ref-like approach: check if viewMode was just set by the reducer
			const savedMode = localStorage.getItem("viewMode");
			const hasSavedPreference = savedMode === "supplier" || savedMode === "manufacturer";

			// If profile just loaded and view mode was set based on subscription (no saved preference)
			// Navigate to dashboard to ensure correct dashboard is shown
			// This is especially important for supplier users who should see supplier dashboard
			if (!hasSavedPreference && props.currentViewMode) {
				// Navigate to dashboard - the correct component will render based on currentViewMode
				// This ensures supplier users see supplier dashboard, manufacturer users see manufacturer dashboard
				if (props.location.pathname !== routes.DASHBOARD) {
					props.history.push(routes.DASHBOARD);
				}
				setHasHandledInitialNavigation(true);
			} else if (hasSavedPreference) {
				// User has a saved preference, mark as handled
				setHasHandledInitialNavigation(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.profile, props.currentViewMode]);

	// 🔑 Helper to check access
	const userHasRouteAccess = (route: IRoute) => {
		const isLoggedIn = !!localStorage.getItem("access_token");

		// Admins and staff always have access
		if (props.profile?.role === "JF_ADMIN" || props.profile?.role === "JF_STAFF") return true;

		// Feature gating for unauthenticated users
		if (!isLoggedIn) {
			// Only allow Overview and Generate
			return route.name === "Overview" || route.name === "Generate";
		}

		// New live permissions gating
		if (props.permissions?.features) {
			const { features } = props.permissions;
			if (route.name === "Guava" && features.has_guava_access === false) return false;
			if (route.name === "Packaging" && features.has_packaging_access === false) return false;
			if (route.name === "Analytics" && features.has_analytics_access === false) return false;
			if (route.name === "Documents" && features.has_documents_access === false) return false;
		}

		if (route.permission_name && props.profile != undefined) {
			return (
				props.profile?.company[route.permission_name] !== "disable" &&
				!route.locked_for?.includes(props.profile?.company.subscription_type)
			);
		}
		return true;
	};


	// 👉 Decide which route set to use based on view mode
	// Use view mode if available, otherwise fall back to subscription_type for backward compatibility
	const currentViewMode = props.currentViewMode ||
		(props.profile?.company?.subscription_type === "supplier" ? "supplier" : "manufacturer");

	let activeRoutes = currentViewMode === "supplier" ? SupplierRoutes : AppRoutes;

	// ✅ Hide Guava if not subscribed
	// if (props.profile?.company?.subscription_type !== "guava") {
	//   activeRoutes = activeRoutes.filter((r) => r.path !== routes.GUAVA);
	// }
	// if (props.profile?.company?.subscription_type !== "guava" && props.profile?.role !== "JF_ADMIN") {
	// 	activeRoutes = activeRoutes.filter((r) => r.path !== routes.GUAVA);
	// }

	return (
		<div
			id="main-wrapper"
			data-theme="light"
			data-layout="vertical"
			data-sidebartype={isSubscribePage ? "mini-sidebar" : sidebarType} // Use mini-sidebar to avoid 'full' desktop margins
			data-sidebar-position="fixed"
			data-header-position="fixed"
			data-boxed-layout="full"
			className={isSubscribePage ? "" : "show-sidebar"}
		>
			{/* Tablet Menu Toggle */}
			<TabletMenuToggle onToggle={handleSidebarToggle} isOpen={isSidebarOpen} />

			{/* Sidebar Overlay */}
			<div className="sidebar-overlay" onClick={() => handleSidebarToggle(false)}></div>

			<div className="d-flex" style={{ width: "100%" }}>
				{/* Sidebar uses correct routes */}
				{!isSubscribePage && <Sidebar routes={activeRoutes} />}

				<div style={{
					width: "100%",
					marginLeft: (isMobile || isSubscribePage) ? 0 : undefined // Remove margin on mobile or subscribe page
				}}>
					{!isProfilePage && !isSubscribePage && <Header />}
					<div className="page-wrapper d-block" style={{ backgroundColor: isSubscribePage ? "#ffffff" : undefined }}>
						<div className="page-content container-fluid" style={{ paddingTop: isSubscribePage ? 0 : undefined }}>
							<Switch>
								<Route exact path={routes.NOTIFICATIONS} component={NotificationsRouter} />
								<Route exact path={routes.SEARCH} component={Search} />

								{/* Account route - always available regardless of view mode */}
								<Route path={routes.ACCOUNT} component={Account} />

								{activeRoutes.map((route) => {
									// Skip Account route as it's handled separately above
									if (route.path === routes.ACCOUNT) {
										return null;
									}

									if (route.redirect) {
										return (
											<Redirect
												from={route.path}
												to={routes.DASHBOARD}
												key={route.path}
											/>
										);
									} else if (route.component) {
										// ⛔ Block Guava if not subscribed
										// if (
										//   route.path === routes.GUAVA &&
										//   props.profile?.company?.subscription_type !== "guava"
										// ) {
										//   return (
										//     <Redirect
										//       from={routes.GUAVA}
										//       to={routes.DASHBOARD}
										//       key="redirect-guava"
										//     />
										//   );
										// }
										// if (
										// 	route.path === routes.GUAVA &&
										// 	props.profile?.company?.subscription_type !== "guava" &&
										// 	props.profile?.role !== "JF_ADMIN"
										// ) {
										// 	return (
										// 		<Redirect from={routes.GUAVA} to={routes.DASHBOARD} key="redirect-guava" />
										// 	);
										// }

										const userHasAccess =
											userHasRouteAccess(route) ||
											props.profile?.role === "JF_ADMIN" ||
											props.profile?.role === "JF_STAFF";

										// For unauthenticated users, only allow Overview and Generate
										const isLoggedIn = !!localStorage.getItem("access_token");
										const isAllowedRoute = route.name === "Overview" || route.name === "Generate";
										const isGuestForbidden = !isLoggedIn && !isAllowedRoute;

										// Determine which dashboard component to use based on view mode
										let componentToRender: React.ComponentType<any> | null = route.component;
										if (route.name === "Overview" && route.path === routes.DASHBOARD) {
											componentToRender = currentViewMode === "supplier"
												? SupplierDashboard as React.ComponentType<any>
												: Dashboard as React.ComponentType<any>;
										}

										return (
											<Route
												path={route.path}
												exact={(route as any).exact || false}
												render={(routeProps) => {
													if (isGuestForbidden) {
														return <Redirect to={routes.DASHBOARD} />;
													}

													const Component = userHasAccess
														? componentToRender
														: route.upgrade_component;

													return Component ? <Component {...routeProps} /> : null;
												}}
												key={route.path}
											/>
										);
									}
									return null;
								})}

								{currentViewMode === "supplier" ? (
									<Redirect exact from="/app" to={routes.DASHBOARD} />
								) : (
									<Redirect exact from="/app" to={routes.DASHBOARD} />
								)}
							</Switch>
						</div>
					</div>
				</div>
			</div>
			{props.profile?.walkthrough ? <Walkthrough /> : <></>}
		</div>
	);
};

const mapStateToProps = (state: IApplicationState) => ({
	role: state.user.role,
	profile: state.user.profile,
	permissions: state.user.permissions,
	currentViewMode: state.user.currentViewMode
});

const mapDispatchToProps = {
	getUserProfile: () => userService.getUserProfile(),
	saveDeviceToken: (token: string) => notificationService.saveDeviceToken(token),
	correctViewMode: (mode: ViewMode) => (dispatch: Dispatch) => {
		dispatch(setViewModeAction(mode));
	}
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
